import { useState, useEffect, createContext, useContext } from 'react';
import { authService } from '../services/authService';
import journalService from '../services/journalService';
import friendsService from '../services/friendsService';
import settingsJournalService from '../services/settingsJournalService';
import { getAuthToken, setAuthToken, clearAllStorage, setRememberMe } from '../utils/authStorage';

const AuthContext = createContext();

const applyTokenToServices = (token) => {
  journalService.updateAuthToken(token);
  friendsService.updateAuthToken(token);
  settingsJournalService.updateAuthToken(token);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = getAuthToken();
        if (token) {
          // Mettre à jour les services avec le token avant les requêtes.
          applyTokenToServices(token);
          const userData = await authService.getCurrentUser();
          setUser(userData);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAllStorage();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password, rememberMe = true) => {
    try {
      const response = await authService.login(email, password);
      setAuthToken(response.token, rememberMe);
      setRememberMe(rememberMe);
      applyTokenToServices(response.token);

      // Hydrater l'utilisateur complet (inclut subscription) pour éviter
      // le clignotement des fonctionnalités premium/publicités après login.
      let hydratedUser = response.user || null;
      try {
        hydratedUser = await authService.getCurrentUser();
      } catch (hydrateError) {
        console.warn('Impossible de rafraîchir le profil utilisateur après login:', hydrateError);
      }

      setUser(hydratedUser);
      return { ...response, user: hydratedUser };
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      setAuthToken(response.token, true);
      setRememberMe(true);
      applyTokenToServices(response.token);

      let hydratedUser = response.user || null;
      try {
        hydratedUser = await authService.getCurrentUser();
      } catch (hydrateError) {
        console.warn('Impossible de rafraîchir le profil utilisateur après inscription:', hydrateError);
      }

      setUser(hydratedUser);
      return { ...response, user: hydratedUser };
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      clearAllStorage();
      journalService.clearAuthToken();
      friendsService.clearAuthToken();
      settingsJournalService.clearAuthToken();
      setLoading(false);
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    login,
    register,
    logout,
    updateUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
